import { addFlashAlert } from "common/components/FlashAlert";
import { IBaseRecord, createAction } from "common/reducers/UtilsTypes";
import { RootState } from "common/reducers";
import { Record } from "immutable";

export const handleErrors = (response: any, flashErrorMessage = true, persistError = false): string[] => {
  let errors: string[] = [];

  console.error(response);

  if (!response) {
    errors = ["Invalid Response - Please try again. If the problem persists, please contact support."];
  } else if (Array.isArray(response)) {
    errors = response;
  } else if (response.status === 500) {
    errors = ["Internal Server Error - Please try again. If the problem persists, please contact support."];
  } else if (Array.isArray(response.data?.errors) || typeof response.data?.errors === "string") {
    errors = errors.concat(response.data.errors);
  } else {
    errors = [
      "Unknown Error - Please check your internet connection. If the problem persists, please contact support.",
    ];
  }

  if (flashErrorMessage) {
    addFlashAlert("danger", errors.join("\n"), persistError);
  }

  return errors;
};

export const handleErrorsWithRejection = (response: any, flashErrorMessage = true): Promise<string[]> =>
  Promise.reject(handleErrors(response, flashErrorMessage));

export { createAction };

export const fetch = <T extends IBaseRecord>(state: RootState, model: string, record: Record<T>): RootState => {
  const id = record.get("id");
  if (state.getIn([model, "byId", id])) {
    return state.setIn([model, "byId", id, "loading"], true);
  }
  return state.setIn([model, "byId", id], record.setIn(["loading"], true));
};

export const receive = <T extends IBaseRecord>(state: RootState, model: string, record: Record<T>): RootState => {
  const id = record.get("id");
  record = record.set("loading", false);
  return state.setIn([model, "byId", id], record).setIn([model, "lastSavedById", id], record);
};

/**
 * Upserts an unsaved (draft) record into `byId` ONLY — never touching `lastSavedById`. This keeps
 * the record droppable/restorable by `reset`: a brand-new draft (no prior lastSaved) is deleted on
 * reset, and an in-place edit of a saved record reverts to its saved copy rather than persisting the
 * edit as the new baseline. When a record already exists in `byId`, only the payload's provided
 * fields are merged over it (so a partial edit preserves untouched fields like designator/ref_id/
 * sort_order); otherwise the payload is hydrated whole via `fromJSON`.
 *
 * @param state The RootState.
 * @param model The store slice key (e.g. "windows", "doors").
 * @param data The partial opening data (only the keys being written); must include `id`.
 * @param fromJSON Factory used to build a fresh record when none exists in `byId`.
 * @returns The RootState with the draft upserted into `[model, "byId", id]`.
 */
export const receiveDraft = <T extends IBaseRecord, D>(
  state: RootState,
  model: string,
  data: Partial<D> & { id: number },
  fromJSON: (data: Partial<D>) => Record<T>,
): RootState => {
  const existing = state.getIn([model, "byId", data.id]) as Record<T> | undefined;
  // The JSON data type D and the record prop type T diverge in name only for the fields a draft
  // edit writes; merge applies just the overlapping keys. Narrow to Partial<T> (not `any`).
  const next = (existing ? existing.merge(data as Partial<T>) : fromJSON(data)).set("loading", false);
  return state.setIn([model, "byId", data.id], next);
};

export const reset = (state: RootState, model: string, id: number | string): RootState => {
  const lastSaved = state.getIn([model, "lastSavedById", id]);

  if (!lastSaved) return state.deleteIn([model, "byId", id]);
  return state.setIn([model, "byId", id], lastSaved);
};

export const clear = (state: RootState, model: string, id: number | string): RootState => {
  return state.deleteIn([model, "lastSavedById", id]).deleteIn([model, "byId", id]);
};

export const loaded = (state: RootState, model: string, id: number | string): RootState => {
  return state.setIn([model, "byId", id, "loading"], false);
};
